import { useEffect, useRef } from 'react'
import { useDataSource } from '@/src/renderer/lib/dataSource'
import { decideFocusRecovery, readFocusHeartbeat, clearFocusHeartbeat } from './focusHeartbeat'
import { useFocus } from './useFocus'

/**
 * **Was ein Absturz hinterlässt, wird beim nächsten Start aufgeräumt.**
 *
 * Stürzt die App ab oder wird sie hart beendet, bleibt ein Abschnitt ohne Ende
 * in der Datenbank stehen. Ohne diesen Aufräumer liefe er beim nächsten Start
 * einfach weiter — und die Nacht zählte als Fokuszeit.
 *
 * Die Regel ist die des Web-Tabs (3d), eine für beide Plattformen: Wer binnen
 * **zwei Minuten** wieder da ist, macht weiter und bekommt das Fokus-Fenster zurück;
 * sonst wird bis zum letzten Lebenszeichen gebucht.
 *
 * Läuft genau einmal, im Hauptfenster. Das Fokus-Fenster selbst ruft ihn nicht auf —
 * sie ist ja der Fall, um den es geht.
 */
export function useFocusRecovery(): void {
  const source = useDataSource()
  const { endFocus } = useFocus()
  const running = source.useRunningFocus()
  const handled = useRef(false)

  useEffect(() => {
    // `undefined` heißt „noch nicht geladen" — nicht „läuft nichts".
    if (running === undefined || handled.current) return
    handled.current = true

    if (!running) {
      clearFocusHeartbeat()
      return
    }

    const decision = decideFocusRecovery(running.startTime, readFocusHeartbeat())

    if (decision.action === 'resume') {
      // Das Fokus-Fenster kommt zurück; das Hauptfenster tritt wieder zurück.
      void window.api?.invoke('focus:started', {})
      return
    }

    // Über `useFocus`, nicht an ihm vorbei: So meldet auch der Aufräumer sein
    // `focus_ended` — mit dem Grund, der es von einer Pause unterscheidet.
    void endFocus({ at: decision.at, reason: 'crash' }).then(clearFocusHeartbeat)
  }, [running, source, endFocus])
}
